import React, { useState, useEffect, useCallback } from 'react';
import { Layout, Menu, Input, Button, Avatar, Badge, Drawer, Row, Col, Dropdown, App } from 'antd';
import { 
  SearchOutlined, 
  HomeOutlined, 
  ReadOutlined, 
  EditOutlined, 
  UserOutlined,
  MenuOutlined,
  BellOutlined,
  HeartOutlined,
  LoginOutlined,
  LogoutOutlined,
  SettingOutlined,
  DownOutlined,
  CrownOutlined,
  FileTextOutlined
} from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import './index.css';

const { Header: AntHeader } = Layout;
const { Search } = Input;

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { message } = App.useApp();
  const [current, setCurrent] = useState('home');
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [user, setUser] = useState(null);
  const [notifications, setNotifications] = useState(3);

  // 使用 useCallback 避免重复创建函数
  const checkUserLogin = useCallback(() => {
    const storedUser = localStorage.getItem('currentUser');
    console.log('🔍 Header: 读取 localStorage 用户数据:', storedUser);
    
    if (!storedUser) {
      console.log('🔍 Header: 没有找到用户数据，设置 user 为 null');
      setUser(null);
      return;
    }

    try {
      const userData = JSON.parse(storedUser);
      console.log('🔍 Header: 解析后的用户数据:', userData);
      
      if (userData.isLoggedIn) {
        console.log('✅ Header: 用户已登录');
        
        // ✅ 直接使用 localStorage 中的数据
        setUser({
          id: userData.id,
          name: userData.nickname || userData.username,
          avatar: userData.avatar || 'https://picsum.photos/40?random=1',
          role: userData.role || 'user',
          username: userData.username,  // 保存用户名
          email: userData.email         // 保存邮箱
        });
        
        console.log('✅ Header: 设置用户状态:', {
          name: userData.nickname || userData.username,
          role: userData.role
        });
      } else {
        console.log('❌ Header: 用户未登录');
        setUser(null);
      }
    } catch (error) {
      console.error('❌ Header: 解析用户数据失败:', error);
      setUser(null);
    }
  }, []);

  // 模拟用户登录状态
  useEffect(() => {
    console.log('🔄 Header: useEffect 运行');
    
    // 初始检查
    checkUserLogin();
    
    // 监听自定义事件
    const handleUserLogin = () => {
      console.log('🔔 Header: 收到 userLogin 事件');
      console.log('当前 localStorage:', localStorage.getItem('currentUser'));
      // 立即检查用户登录状态
      setTimeout(() => {
        checkUserLogin();
      }, 0);
    };
    
    // 监听 storage 事件（用于跨标签页）
    const handleStorageChange = (e) => {
      console.log('🔄 Header: storage 事件触发:', e.key);
      if (e.key === 'currentUser') {
        checkUserLogin();
      }
    };

    window.addEventListener('userLogin', handleUserLogin);
    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      window.removeEventListener('userLogin', handleUserLogin);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [checkUserLogin]);

  // 根据路由设置当前选中菜单
  useEffect(() => {
    const path = location.pathname;
    if (path === '/') setCurrent('home');
    else if (path.startsWith('/archive')) setCurrent('archive');
    else if (path.startsWith('/about')) setCurrent('about');
    else if (path.startsWith('/write')) setCurrent('write');
  }, [location]);

  const menuItems = [
    {
      key: 'home',
      icon: <HomeOutlined />,
      label: '首页',
      onClick: () => navigate('/')
    },
    {
      key: 'archive',
      icon: <ReadOutlined />,
      label: '文章归档',
      onClick: () => navigate('/archive')
    },
    {
      key: 'write',
      icon: <EditOutlined />,
      label: '写文章',
      onClick: () => navigate('/write')
    },
    {
      key: 'drafts',
      icon: <FileTextOutlined />,
      label: '草稿箱',
      onClick: () => navigate('/drafts')
    },
    {
      key: 'admin',
      icon: <CrownOutlined />,
      label: '管理面板',
      onClick: () => navigate('/admin')
    },
    {
      key: 'about',
      icon: <UserOutlined />,
      label: '关于我',
      onClick: () => navigate('/about')
    },
  ];

  // 在渲染时动态过滤
  const filteredMenuItems = menuItems.filter(item => {
    // 如果是管理员菜单，只对管理员显示
    if (item.key === 'admin') {
      console.log('🔍 检查管理员菜单权限:', { 
        userRole: user?.role, 
        isAdmin: user?.role === 'admin' 
      });
      return user?.role === 'admin';
    }
    return true;
  });

  // 添加管理员菜单显示的调试
  useEffect(() => {
    if (user) {
      console.log('👤 当前用户:', user);
      console.log('🔍 管理员菜单是否显示:', user?.role === 'admin');
    }
  }, [user]);

  const handleLogout = () => {
    setUser(null);
    localStorage.removeItem('currentUser');  // 清除 localStorage
    localStorage.removeItem('rememberUser'); // 清除记住我
    message.success('已退出登录');
    navigate('/');
  };

  const userMenuItems = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: '个人中心',
      onClick: () => navigate('/profile')
    },
    {
      key: 'settings',
      icon: <SettingOutlined />,
      label: '设置',
      onClick: () => navigate('/settings')
    },
    {
      type: 'divider'
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: '退出登录',
      onClick: handleLogout
    }
  ];

  const onSearch = (value) => {
    if (!value.trim()) {
      message.warning('请输入搜索内容');
      return;
    }
    message.info(`搜索: ${value}`);
    // 这里可以跳转到搜索结果页
    navigate(`/search?q=${encodeURIComponent(value)}`);
  };

  const showDrawer = () => {
    setDrawerVisible(true);
  };

  const onCloseDrawer = () => {
    setDrawerVisible(false);
  };

  const handleLogin = () => {
    navigate('/auth');
    console.log('登录按钮被点击了');
  };

  const handleLikeClick = () => {
    message.success('感谢你的喜欢！❤️');
  };

  const handleNotificationClick = () => {
    message.info('暂无新通知');
    setNotifications(0);
  };

  const handleLogoClick = () => {
    navigate('/');
  };

return (
  <>
    <AntHeader className="cute-header">
      <Row justify="space-between" align="middle" className="header-container">
        {/* 左侧Logo区域 */}
        <Col xs={6} md={4}>
          <div className="logo" onClick={handleLogoClick}>
            <div className="logo-icon float-animation">🐰</div>
            <span className="logo-text">My blog</span>
          </div>
        </Col>
        
        {/* 中间导航菜单 - 桌面端显示 */}
        <Col xs={0} md={12} lg={10}>
          {/* 使用过滤后的菜单项 */}
          <Menu
            mode="horizontal"
            selectedKeys={[current]}
            onClick={(e) => {
              setCurrent(e.key);
              const item = filteredMenuItems.find(m => m.key === e.key);
              item?.onClick();
            }}
            // 使用过滤后的 items
            items={filteredMenuItems}
            className="main-menu"
          />
        </Col>
        
        {/* 右侧操作区域 */}
        <Col xs={12} md={8} lg={10}>
          <div className="header-actions">
            {/* 搜索框 - 桌面端显示 */}
            <div className="desktop-search">
              <Search
                placeholder="搜索文章..."
                onSearch={onSearch}
                enterButton={<SearchOutlined />}
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                className="header-search"
              />
            </div>

            {/* 通知 */}
            <Badge count={notifications} className="notification-badge">
              <Button 
                type="text" 
                icon={<BellOutlined />} 
                className="icon-btn pulse-animation"
                onClick={handleNotificationClick}
              />
            </Badge>

            {/* 喜欢按钮 */}
            <Button 
              type="primary" 
              icon={<HeartOutlined />} 
              className="like-btn"
              onClick={handleLikeClick}
            >
              喜欢
            </Button>

            {/* 用户头像/登录 */}
            {user ? (
              <Dropdown
                menu={{
                  items: userMenuItems
                }}
                placement="bottomRight"
              >
                <div className="user-avatar-wrapper">
                  <Avatar 
                    src={user.avatar} 
                    className="user-avatar"
                    size="large"
                  />
                  <span className="user-name">{user.name}</span>
                  <DownOutlined className="dropdown-icon" />
                </div>
              </Dropdown>
            ) : (
              <Button 
                type="default" 
                icon={<LoginOutlined />} 
                className="login-btn"
                onClick={handleLogin}
              >
                登录
              </Button>
            )}

            {/* 移动端菜单按钮 */}
            <Button 
              type="text" 
              icon={<MenuOutlined />} 
              className="mobile-menu-btn"
              onClick={showDrawer}
            />
          </div>
        </Col>
      </Row>
    </AntHeader>

    {/* 移动端抽屉菜单 */}
    <Drawer
      title={
        <div className="drawer-header">
          <div className="drawer-logo">
            <div className="logo-icon">🐰</div>
            <span className="logo-text">My blog</span>
          </div>
        </div>
      }
      placement="right"
      onClose={onCloseDrawer}
      open={drawerVisible}
      className="mobile-drawer"
    >
      {/* 移动端搜索框 */}
      <div className="mobile-search">
        <Search
          placeholder="搜索文章..."
          onSearch={onSearch}
          enterButton={<SearchOutlined />}
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
          className="drawer-search"
        />
      </div>

      {/* 移动端菜单 */}
      <Menu
        mode="vertical"
        selectedKeys={[current]}
        onClick={(e) => {
          setCurrent(e.key);
          onCloseDrawer();
          const item = filteredMenuItems.find(m => m.key === e.key);
          item?.onClick();
        }}
        items={filteredMenuItems}
        className="drawer-menu"
      />

      <div className="drawer-actions">
        {user ? (
          <>
            <div className="drawer-user">
              <Avatar 
                src={user.avatar} 
                className="drawer-avatar"
                size={60}
              />
              <div className="drawer-user-info">
                <h4>{user.name}</h4>
                <p>{user.role === 'admin' ? '管理员' : '普通用户'}</p>
              </div>
            </div>
            <div className="drawer-buttons">
              <Button block icon={<UserOutlined />} onClick={() => navigate('/profile')}>
                个人中心
              </Button>
              <Button block icon={<SettingOutlined />} onClick={() => navigate('/settings')}>
                设置
              </Button>
              <Button block danger icon={<LogoutOutlined />} onClick={handleLogout}>
                退出登录
              </Button>
            </div>
          </>
        ) : (
          <Button 
            block 
            type="primary" 
            icon={<LoginOutlined />} 
            onClick={handleLogin}
            className="drawer-login-btn"
          >
            登录/注册
          </Button>
        )}

        <Button 
          block 
          type="primary" 
          icon={<HeartOutlined />} 
          onClick={handleLikeClick}
          className="drawer-like-btn"
        >
          点赞支持
        </Button>
      </div>
    </Drawer>
  </>
);
};

export default Header;